import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet,
  Download,
  Plus,
  PieChart,
  DollarSign,
  Calendar
} from 'lucide-react';
import { currencySymbols } from '@/types';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface ExpenseCategory {
  id: string;
  name: string;
  parent_id: string | null;
}

interface Expense {
  id: string;
  category: string;
  amount: number;
  expense_date: string;
  description?: string;
}

// Supabase returns joined relations as arrays. We accept both the array
// form (what the query returns) and the single-object form so that the
// type is compatible with either shape without an unsafe cast.
type MembersField = { full_name: string }[] | { full_name: string } | null;

interface DuesTransaction {
  id: string;
  payment_date: string;
  amount: number;
  payment_method?: string;
  reference_number?: string;
  members?: MembersField;
}

interface DonationTransaction {
  amount: number;
  donation_date?: string;
  donor_name?: string | null;
  members?: MembersField;
}

interface FinancialData {
  income: {
    dues: number;
    donations: number;
    other: number;
    total: number;
  };
  expenses: {
    byCategory: Record<string, number>;
    total: number;
  };
  transactions: {
    dues: DuesTransaction[];
    donations: DonationTransaction[];
    expenses: Expense[];
  };
}

interface OutstandingMember {
  id: string;
  full_name: string;
  title?: string;
  dues_balance: number;
}

interface OutstandingPledge {
  id: string;
  member_name?: string;
  donor_name?: string;
  amount: number;
  pledge_due_date?: string;
  event_name?: string;
}

interface OutstandingData {
  members: OutstandingMember[];
  totalDuesOwed: number;
  pledges: OutstandingPledge[];
  totalPledgesOwed: number;
}

/** Convert an array of objects to a CSV string and trigger a browser download. */
function downloadCSV(filename: string, rows: Record<string, string | number>[]) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((h) => {
          const val = String(row[h] ?? '');
          return /[",\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;
        })
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Safely extract full_name from a MembersField (array or single object).
 *
 * The parameter is `MembersField | undefined` — not just `MembersField` —
 * because the `members` property on both DuesTransaction and
 * DonationTransaction is optional (`members?: MembersField`). TypeScript
 * infers the call-site type as `MembersField | undefined`, so the parameter
 * must accept that union or the build fails with TS2345.
 */
function extractFullName(members: MembersField | undefined): string | undefined {
  if (!members) return undefined;
  if (Array.isArray(members)) return members[0]?.full_name;
  return members.full_name;
}

/** Resolve the display name for a dues payment — member name or fallback. */
function resolveDuesName(p: DuesTransaction): string {
  return extractFullName(p.members) ?? 'Unknown Member';
}

/** Resolve the display name for a donation — member name, donor_name, or fallback. */
function resolveDonorName(d: DonationTransaction): string {
  return extractFullName(d.members) ?? d.donor_name ?? 'Anonymous';
}

export default function Finances() {
  const { t } = useTranslation();
  const { currentOrganization } = useAppStore();
  
  const [isLoading, setIsLoading] = useState(true);
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const [outstandingData, setOutstandingData] = useState<OutstandingData | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Expense form
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expenseDescription, setExpenseDescription] = useState('');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  const fetchCategories = useCallback(async () => {
    if (!currentOrganization) return;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(currentOrganization.id)) return;
    try {
      const { data } = await supabase
        .from('expense_categories')
        .select('id, name, parent_id')
        .or(`organization_id.eq.${currentOrganization.id},organization_id.is.null`)
        .eq('is_active', true)
        .order('name');

      setCategories(data || []);
      const firstChild = (data || []).find(c => c.parent_id !== null);
      if (firstChild) {
        const parentCat = (data || []).find(c => c.id === firstChild.parent_id);
        if (parentCat) setExpenseCategory(`${parentCat.name} > ${firstChild.name}`);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [currentOrganization]);

  const fetchFinancialData = useCallback(async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      let startDate: Date;
      let endDate: Date = new Date();

      switch (selectedPeriod) {
        case 'this_month':
          startDate = startOfMonth(new Date());
          endDate = endOfMonth(new Date());
          break;
        case 'last_month':
          startDate = startOfMonth(subMonths(new Date(), 1));
          endDate = endOfMonth(subMonths(new Date(), 1));
          break;
        case 'this_year':
          startDate = new Date(new Date().getFullYear(), 0, 1);
          endDate = new Date(new Date().getFullYear(), 11, 31);
          break;
        case 'custom_range':
          if (!customStartDate || !customEndDate) return;
          if (new Date(customStartDate) > new Date(customEndDate)) {
            toast.error('Start date must be before end date');
            return;
          }
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          break;
        case 'all_time':
        default:
          startDate = new Date(2000, 0, 1);
      }

      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');

      // Fetch dues payments — join members to get full_name
      const { data: duesData } = await supabase
        .from('payments')
        .select('id, payment_date, amount, payment_method, reference_number, members(full_name)')
        .eq('organization_id', currentOrganization.id)
        .eq('payment_type', 'dues')
        .gte('payment_date', startStr)
        .lte('payment_date', endStr)
        .order('payment_date', { ascending: false });

      // Fetch donations — include donor_name and join members for member-donors
      const { data: donationsData } = await supabase
        .from('donations')
        .select('amount, donor_name, donation_date, members(full_name), donation_events!inner(organization_id)')
        .eq('donation_events.organization_id', currentOrganization.id)
        .gte('donation_date', startStr)
        .lte('donation_date', endStr)
        .order('donation_date', { ascending: false });

      // Fetch expenses
      const { data: expensesData } = await supabase
        .from('expenses')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .gte('expense_date', startStr)
        .lte('expense_date', endStr)
        .order('expense_date', { ascending: false });

      const duesTotal = duesData?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
      const donationsTotal = donationsData?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;
      
      const expensesByCategory: Record<string, number> = {};
      expensesData?.forEach((e) => {
        expensesByCategory[e.category] = (expensesByCategory[e.category] || 0) + (e.amount || 0);
      });
      const expensesTotal = Object.values(expensesByCategory).reduce((a, b) => a + b, 0);

      setFinancialData({
        income: {
          dues: duesTotal,
          donations: donationsTotal,
          other: 0,
          total: duesTotal + donationsTotal,
        },
        expenses: {
          byCategory: expensesByCategory,
          total: expensesTotal,
        },
        transactions: {
          // Cast via unknown first so TypeScript accepts the Supabase
          // return type (which uses `any` internally) into our stricter
          // interface that now correctly models the array join shape.
          dues: (duesData as unknown as DuesTransaction[]) || [],
          donations: (donationsData as unknown as DonationTransaction[]) || [],
          expenses: expensesData || [],
        },
      });
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization, selectedPeriod, customStartDate, customEndDate, t]);

  const fetchOutstandingData = useCallback(async () => {
    if (!currentOrganization) return;

    try {
      const { data: membersData, error: membersError } = await supabase
        .from('members')
        .select('id, full_name, title, dues_balance')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'active')
        .gt('dues_balance', 0)
        .order('full_name');

      if (membersError) throw membersError;

      const { data: pledgesData, error: pledgesError } = await supabase
        .from('donations')
        .select('id, amount, pledge_due_date, donor_name, member:members(full_name), donation_events!inner(organization_id, name)')
        .eq('donation_events.organization_id', currentOrganization.id)
        .eq('is_pledge', true)
        .order('pledge_due_date', { ascending: true });

      if (pledgesError) throw pledgesError;

      const members = (membersData as OutstandingMember[]) || [];
      const totalDuesOwed = members.reduce((sum, m) => sum + (m.dues_balance || 0), 0);

      const pledges = (pledgesData || []).map((p) => ({
        id: p.id,
        amount: p.amount,
        pledge_due_date: p.pledge_due_date,
        donor_name: p.donor_name,
        member_name: Array.isArray(p.member) ? p.member[0]?.full_name : (p.member as { full_name?: string } | null)?.full_name,
        event_name: Array.isArray(p.donation_events) ? p.donation_events[0]?.name : (p.donation_events as { name?: string } | null)?.name,
      })) as OutstandingPledge[];

      const totalPledgesOwed = pledges.reduce((sum, p) => sum + (p.amount || 0), 0);
      setOutstandingData({ members, totalDuesOwed, pledges, totalPledgesOwed });
    } catch (error) {
      console.error('Error fetching outstanding data:', error);
      toast.error(t('errors.generic'));
    }
  }, [currentOrganization, t]);

  useEffect(() => {
    fetchCategories();
    fetchOutstandingData();
    if (selectedPeriod !== 'custom_range') {
      fetchFinancialData();
    }
  }, [fetchCategories, fetchFinancialData, fetchOutstandingData, selectedPeriod]);

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!expenseCategory) {
      toast.error('Please select a category');
      return;
    }
    if (!expenseAmount) {
      toast.error('Please enter an amount');
      return;
    }
    if (expenseCategory.startsWith('Miscellaneous') && !expenseDescription.trim()) {
      toast.error('A description is required for Miscellaneous expenses');
      return;
    }

    if (!currentOrganization) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('expenses')
        .insert({
          organization_id: currentOrganization.id,
          category: expenseCategory,
          amount: parseFloat(expenseAmount),
          expense_date: expenseDate,
          description: expenseDescription || null,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Expense added successfully');
      setIsAddExpenseOpen(false);
      
      // Reset form
      setExpenseAmount('');
      setExpenseDescription('');
      setExpenseDate(format(new Date(), 'yyyy-MM-dd'));
      
      fetchFinancialData();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExport = () => {
    if (!financialData) {
      toast.error('No financial data available to export');
      return;
    }

    const orgName = currentOrganization?.name ?? 'Organisation';
    const periodLabel = selectedPeriod === 'custom_range' && customStartDate && customEndDate
      ? `${customStartDate}_to_${customEndDate}`
      : selectedPeriod.replace(/_/g, ' ');
    const exportedAt = format(new Date(), 'yyyy-MM-dd');
    const filename = `${orgName}_finances_${periodLabel}_${exportedAt}.csv`.replace(/\s+/g, '_');

    const rows: Record<string, string | number>[] = [];

    // ── Summary ───────────────────────────────────────────────────
    rows.push({ Section: '=== SUMMARY ===', Date: '', Name: '', Type: '', Category: '', Description: '', Amount: '', 'Payment Method': '', Reference: '' });
    rows.push({ Section: 'Summary', Date: exportedAt, Name: '', Type: 'Total Income',   Category: '', Description: '', Amount: financialData.income.total, 'Payment Method': '', Reference: '' });
    rows.push({ Section: 'Summary', Date: exportedAt, Name: '', Type: 'Dues Income',    Category: '', Description: '', Amount: financialData.income.dues,  'Payment Method': '', Reference: '' });
    rows.push({ Section: 'Summary', Date: exportedAt, Name: '', Type: 'Donations',      Category: '', Description: '', Amount: financialData.income.donations, 'Payment Method': '', Reference: '' });
    rows.push({ Section: 'Summary', Date: exportedAt, Name: '', Type: 'Total Expenses', Category: '', Description: '', Amount: financialData.expenses.total, 'Payment Method': '', Reference: '' });
    rows.push({
      Section: 'Summary',
      Date: exportedAt,
      Name: '',
      Type: (financialData.income.total - financialData.expenses.total) >= 0 ? 'Surplus' : 'Deficit',
      Category: '',
      Description: '',
      Amount: financialData.income.total - financialData.expenses.total,
      'Payment Method': '',
      Reference: '',
    });

    // ── Expenses by category ─────────────────────────────────────
    rows.push({ Section: '=== EXPENSES BY CATEGORY ===', Date: '', Name: '', Type: '', Category: '', Description: '', Amount: '', 'Payment Method': '', Reference: '' });
    Object.entries(financialData.expenses.byCategory).forEach(([cat, amt]) => {
      rows.push({ Section: 'Expense Category', Date: exportedAt, Name: '', Type: 'Expense', Category: cat, Description: '', Amount: amt, 'Payment Method': '', Reference: '' });
    });

    // ── Dues transactions (with member names) ────────────────────
    rows.push({ Section: '=== DUES PAYMENTS ===', Date: '', Name: '', Type: '', Category: '', Description: '', Amount: '', 'Payment Method': '', Reference: '' });
    financialData.transactions.dues.forEach((p) => {
      rows.push({
        Section: 'Dues Payment',
        Date: p.payment_date,
        Name: resolveDuesName(p),
        Type: 'Dues Payment',
        Category: 'Dues',
        Description: '',
        Amount: p.amount,
        'Payment Method': p.payment_method ?? '',
        Reference: p.reference_number ?? '',
      });
    });

    // ── Donations (with donor names) ─────────────────────────────
    rows.push({ Section: '=== DONATIONS ===', Date: '', Name: '', Type: '', Category: '', Description: '', Amount: '', 'Payment Method': '', Reference: '' });
    financialData.transactions.donations.forEach((d) => {
      rows.push({
        Section: 'Donation',
        Date: d.donation_date ?? exportedAt,
        Name: resolveDonorName(d),
        Type: 'Donation',
        Category: 'Donations',
        Description: '',
        Amount: d.amount,
        'Payment Method': '',
        Reference: '',
      });
    });

    // ── Expense transactions ──────────────────────────────────────
    rows.push({ Section: '=== EXPENSE TRANSACTIONS ===', Date: '', Name: '', Type: '', Category: '', Description: '', Amount: '', 'Payment Method': '', Reference: '' });
    financialData.transactions.expenses.forEach((e) => {
      rows.push({
        Section: 'Expense',
        Date: e.expense_date,
        Name: '',
        Type: 'Expense',
        Category: e.category,
        Description: e.description ?? '',
        Amount: -e.amount,
        'Payment Method': '',
        Reference: '',
      });
    });

    downloadCSV(filename, rows);
    toast.success('Report exported successfully');
  };

  const netAmount = (financialData?.income.total || 0) - (financialData?.expenses.total || 0);
  const isSurplus = netAmount >= 0;

  const parentCategories = categories.filter(c => c.parent_id === null);
  const childCategories = categories.filter(c => c.parent_id !== null);

  const childrenByParent = new Map<string, ExpenseCategory[]>();
  childCategories.forEach((c) => {
    if (c.parent_id) {
      const arr = childrenByParent.get(c.parent_id) ?? [];
      arr.push(c);
      childrenByParent.set(c.parent_id, arr);
    }
  });

  const isMiscellaneousCategory = expenseCategory.startsWith('Miscellaneous');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('navigation.finances')}</h1>
          <p className="text-muted-foreground">Financial overview and reports</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Select value={selectedPeriod} onValueChange={(value) => {
            if (value === 'custom_range' && !customStartDate && !customEndDate) {
              setCustomStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
              setCustomEndDate(format(new Date(), 'yyyy-MM-dd'));
            }
            setSelectedPeriod(value);
          }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem value="custom_range">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {selectedPeriod === 'custom_range' && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input
                  type="date"
                  className="w-36"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input
                  type="date"
                  className="w-36"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                disabled={!customStartDate || !customEndDate || new Date(customStartDate) > new Date(customEndDate)}
                onClick={fetchFinancialData}
              >
                Apply
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleExport} disabled={isLoading || !financialData}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Income"
          value={`${currencySymbol}${(financialData?.income.total || 0).toLocaleString()}`}
          icon={TrendingUp}
          trend="up"
          isLoading={isLoading}
        />
        <SummaryCard
          title="Total Expenses"
          value={`${currencySymbol}${(financialData?.expenses.total || 0).toLocaleString()}`}
          icon={TrendingDown}
          trend="down"
          isLoading={isLoading}
        />
        <SummaryCard
          title={isSurplus ? 'Surplus' : 'Deficit'}
          value={`${currencySymbol}${Math.abs(netAmount).toLocaleString()}`}
          icon={Wallet}
          trend={isSurplus ? 'up' : 'down'}
          isLoading={isLoading}
          valueClassName={isSurplus ? 'text-green-600' : 'text-red-600'}
        />
        <SummaryCard
          title="Dues Collected"
          value={`${currencySymbol}${(financialData?.income.dues || 0).toLocaleString()}`}
          icon={DollarSign}
          isLoading={isLoading}
        />
      </div>

      {/* Detailed Breakdown */}
      <Tabs defaultValue="income">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="income">Income</TabsTrigger>
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
        </TabsList>

        <TabsContent value="income" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Income Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <IncomeRow 
                  label="Dues Collection" 
                  amount={financialData?.income.dues || 0} 
                  total={financialData?.income.total || 1}
                  currencySymbol={currencySymbol}
                />
                <IncomeRow 
                  label="Donations" 
                  amount={financialData?.income.donations || 0} 
                  total={financialData?.income.total || 1}
                  currencySymbol={currencySymbol}
                />
                <div className="pt-4 border-t flex items-center justify-between">
                  <span className="font-semibold">Total Income</span>
                  <span className="font-bold text-lg text-green-600">
                    {currencySymbol}{(financialData?.income.total || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expenses" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setIsAddExpenseOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </div>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Expenses by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(financialData?.expenses.byCategory || {}).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(financialData?.expenses.byCategory || {}).map(([category, amount]) => (
                    <IncomeRow 
                      key={category}
                      label={category} 
                      amount={amount} 
                      total={financialData?.expenses.total || 1}
                      currencySymbol={currencySymbol}
                    />
                  ))}
                  <div className="pt-4 border-t flex items-center justify-between">
                    <span className="font-semibold">Total Expenses</span>
                    <span className="font-bold text-lg text-red-600">
                      {currencySymbol}{(financialData?.expenses.total || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <PieChart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No expenses recorded for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Dues Transactions */}
                {financialData?.transactions.dues.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium">{resolveDuesName(payment)}</p>
                      <p className="text-sm text-muted-foreground">
                        Dues Payment · {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                        {payment.payment_method && ` · ${payment.payment_method}`}
                      </p>
                    </div>
                    <span className="font-bold text-green-600">
                      +{currencySymbol}{payment.amount.toLocaleString()}
                    </span>
                  </div>
                ))}

                {/* Donation Transactions */}
                {financialData?.transactions.donations.map((donation, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium">{resolveDonorName(donation)}</p>
                      <p className="text-sm text-muted-foreground">
                        Donation{donation.donation_date ? ` · ${format(new Date(donation.donation_date), 'MMM dd, yyyy')}` : ''}
                      </p>
                    </div>
                    <span className="font-bold text-blue-600">
                      +{currencySymbol}{donation.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
                
                {/* Expense Transactions */}
                {financialData?.transactions.expenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium">{expense.category}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(expense.expense_date), 'MMM dd, yyyy')}
                        {expense.description && ` · ${expense.description}`}
                      </p>
                    </div>
                    <span className="font-bold text-red-600">
                      -{currencySymbol}{expense.amount.toLocaleString()}
                    </span>
                  </div>
                ))}

                {financialData?.transactions.dues.length === 0 && 
                 financialData?.transactions.donations.length === 0 &&
                 financialData?.transactions.expenses.length === 0 && (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No transactions for this period</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="outstanding" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <p className="text-sm text-red-700">Total Dues Owed</p>
                <p className="text-2xl font-bold text-red-700">
                  {currencySymbol}{(outstandingData?.totalDuesOwed || 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm text-amber-700">Total Pledges Outstanding</p>
                <p className="text-2xl font-bold text-amber-700">
                  {currencySymbol}{(outstandingData?.totalPledgesOwed || 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Outstanding Dues</CardTitle>
            </CardHeader>
            <CardContent>
              {outstandingData?.members.length ? (
                <div className="space-y-3">
                  {outstandingData.members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{member.full_name}</p>
                        <p className="text-sm text-muted-foreground">{member.title || 'Member'}</p>
                      </div>
                      <span className="font-bold text-red-600">
                        {currencySymbol}{member.dues_balance.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">All members are up to date with dues.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Outstanding Pledges</CardTitle>
            </CardHeader>
            <CardContent>
              {outstandingData?.pledges.length ? (
                <div className="space-y-3">
                  {outstandingData.pledges.map((pledge) => {
                    const dueDate = pledge.pledge_due_date ? new Date(pledge.pledge_due_date) : null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const oneWeekFromNow = new Date();
                    oneWeekFromNow.setHours(0, 0, 0, 0);
                    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
                    if (dueDate) {
                      dueDate.setHours(0, 0, 0, 0);
                    }
                    const dueDateClass = dueDate
                      ? dueDate < today
                        ? 'text-red-600'
                        : dueDate <= oneWeekFromNow
                          ? 'text-amber-600'
                          : 'text-muted-foreground'
                      : 'text-muted-foreground';

                    return (
                      <div key={pledge.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">{pledge.member_name || pledge.donor_name || 'Anonymous'}</p>
                          <p className="text-sm text-muted-foreground">{pledge.event_name || 'Donation Event'}</p>
                          <p className={`text-xs ${dueDateClass}`}>
                            {pledge.pledge_due_date
                              ? `Due: ${format(new Date(pledge.pledge_due_date), 'MMM dd, yyyy')}`
                              : 'No due date'}
                          </p>
                        </div>
                        <span className="font-bold text-amber-600">
                          {currencySymbol}{(pledge.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground">No outstanding pledges.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Expense Dialog */}
      <Dialog open={isAddExpenseOpen} onOpenChange={setIsAddExpenseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>Record a new expense</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddExpense} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={expenseCategory} onValueChange={setExpenseCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {parentCategories.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-muted-foreground">No categories available</p>
                  ) : (
                    parentCategories.map((parent) => {
                      const subs = childrenByParent.get(parent.id) ?? [];
                      if (subs.length === 0) return null;
                      return (
                        <div key={parent.id}>
                          <div className="px-2 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {parent.name}
                          </div>
                          {subs.map((sub) => (
                            <SelectItem
                              key={sub.id}
                              value={`${parent.name} > ${sub.name}`}
                              className="pl-5"
                            >
                              {sub.name}
                            </SelectItem>
                          ))}
                        </div>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Description{' '}
                {isMiscellaneousCategory
                  ? <span className="text-red-500 text-xs">* required</span>
                  : <span className="text-muted-foreground text-xs">(Optional)</span>}
              </Label>
              <Input
                placeholder="Expense description"
                value={expenseDescription}
                onChange={(e) => setExpenseDescription(e.target.value)}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsAddExpenseOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Add Expense'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  trend?: 'up' | 'down';
  isLoading: boolean;
  valueClassName?: string;
}

function SummaryCard({ title, value, icon: Icon, trend, isLoading, valueClassName }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-8 w-24 mt-1" />
            ) : (
              <p className={`text-xl font-bold ${valueClassName}`}>{value}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            {trend && (
              <span className={`text-xs ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                {trend === 'up' ? '↑' : '↓'}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface IncomeRowProps {
  label: string;
  amount: number;
  total: number;
  currencySymbol: string;
}

function IncomeRow({ label, amount, total, currencySymbol }: IncomeRowProps) {
  const percentage = total > 0 ? Math.round((amount / total) * 100) : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span>{label}</span>
        <span className="font-medium">{currencySymbol}{amount.toLocaleString()} ({percentage}%)</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className="h-full bg-primary rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
